import type { DepartmentJSONObject } from '@ikegps/core-api';
import { type IObservableArray, observable, override, makeObservable } from 'mobx';
import { MaterialIcon } from '../../components/icon/icons';
import { Icon } from '../../components/icon/index';
import { RadioInput } from '../../components/input/radio.input';
import { RadioInputState } from '../../components/input/radio.input.state';
import { IMithrilComponent } from '../../components/mithril.component';
import { ModalService } from '../../components/modal/modal.service';
import { ModalFormComponent, type ModalFormComponentCtx } from '../../components/modal.form/modal.form.component';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n/index';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { IkePageName } from '../../page/page';
import { Services } from '../../services/index';
import { CSS, Style } from './department.selector.style';
import { getLongRunningTaskManager, LongRunningTaskManagerState } from '../long.running.task.manager';

addStyles(Style);

export class DepartmentSelector extends IMithrilComponent {
	modal: DepartmentSelectorModal;

	constructor() {
		super();

		this.modal = new DepartmentSelectorModal({
			title: T.DEPARTMENT_SELECTOR_TITLE,
			confirm: T.DEPARTMENT_SELECTOR_ACCEPT,
			success: { key: T.DEPARTMENT_SELECTOR_SUCCESS },
		});
	}

	get nodes(): MithrilVNode {
		if (Services.Department.current == null) {
			return null;
		}

		const oneDepartment = Services.Department.departments.length < 2;
		const isDepartmentDisabled = this.modal.isDepartmentDisabled(Services.Department.current);
		const departmentName = isDepartmentDisabled
			? `${Services.Department.current.displayName} (${Translate(T.DEPARTMENT_SELECTOR_DISABLED)})`
			: Services.Department.current.displayName;
		const departmentClass = isDepartmentDisabled ? `${CSS.department} ${CSS.departmentDisabled}` : CSS.department;

		return m('div', { className: CSS.container }, [
			m('button', { className: `mdl-button ${CSS.btn}`, disabled: oneDepartment, onclick: () => this.openModal() }, [
				Icon.view(MaterialIcon.DEPARTMENT, { className: CSS.icon }),
				m('span', { className: departmentClass }, departmentName),
			]),
		]);
	}

	openModal() {
		this.modal.open();
	}
}

export class DepartmentSelectorModal extends ModalFormComponent {
	states: IObservableArray<RadioInputState> = observable.array([], { deep: false });

	constructor(ctx: ModalFormComponentCtx) {
		super(ctx);
		makeObservable(this);
	}

	@override override open() {
		if (ModalService.isOpen) {
			return;
		}

		const currentDepartmentId = Services.Department.current?.objectId;
		const activeTasks = getLongRunningTaskManager().state.activeTasks.filter(
			(t) => t.departmentId === currentDepartmentId
		);
		// IKE Reports / PDF are handled on the front end, so do actually need to block while they are going.
		const hasPdfExport = activeTasks.some(
			(t) => LongRunningTaskManagerState.isExportTask(t) && t.exportType.includes(Translate(T.EXPORT_REPORT))
		);

		if (activeTasks.length > 0) {
			if (hasPdfExport) {
				ModalService.open({
					title: T.DEPARTMENT_SELECTOR_TITLE,
					content: Translate(T.DEPARTMENT_SWITCH_PDF_EXPORT_IN_PROGRESS_CONFIRM),
					okName: T.CANCEL,
				});
			} else {
				ModalService.open({
					title: T.DEPARTMENT_SELECTOR_TITLE,
					content: Translate(T.DEPARTMENT_SWITCH_EXPORT_IN_PROGRESS_CONFIRM),
					hasCancel: true,
				}).then((response) => {
					if (response.ok) {
						super.open();
						this.states.replace(this.getStates());
					}
				});
			}
		} else {
			super.open();
			this.states.replace(this.getStates());
		}
	}

	getStates(): RadioInputState[] {
		const states: RadioInputState[] = [];
		const radioGroup = 'department-select';

		const currentDepartmentId = Services.Department.current.objectId;

		for (const department of Services.Department.departments) {
			const isCurrentDepartment = department.objectId === currentDepartmentId;
			const isDepartmentDisabled = this.isDepartmentDisabled(department);
			const departmentNameCurrent = isCurrentDepartment
				? `${department.displayName} (${Translate(T.DEPARTMENT_SELECTOR_CURRENT)})`
				: department.displayName;
			const departmentName = isDepartmentDisabled
				? `${departmentNameCurrent} (${Translate(T.DEPARTMENT_SELECTOR_DISABLED)})`
				: departmentNameCurrent;

			const state = new RadioInputState(departmentName, radioGroup);
			state.id = department.objectId;

			if (isCurrentDepartment) {
				state.setValue(true);
			}

			if (isDepartmentDisabled) {
				state.setDisabled(true);
			}

			states.push(state);
		}

		return states;
	}

	get nodes(): MithrilVNode {
		const children = [];

		for (const state of this.states) {
			children.push(RadioInput.view(state));
		}

		return m(
			'div',
			{
				className: CSS.departmentOptions,
			},
			children
		);
	}

	override async submit() {
		let departmentId: string | null = null;
		for (const state of this.states) {
			if (state.value) {
				departmentId = state.id;
				break;
			}
		}

		if (departmentId == null) {
			throw new Error('No department selected');
		}

		await Services.UserSettings.setDepartment(departmentId);

		if (!Services.Route.supportDepartmentChange()) {
			Services.Route.go(IkePageName.map);
		}
	}

	isDepartmentDisabled(department: DepartmentJSONObject): boolean {
		if (department.disableAt && Date.parse(department.disableAt) < Date.now()) {
			return true;
		}

		return false;
	}
}
